import { Check, Loader2, CheckCircle2, X, Save } from 'lucide-react';

interface SalesMaterialsBottomBarProps {
  completedCount: number;
  totalCount: number;
  hasChanges: boolean;
  saving: boolean;
  saved: boolean;
  onSave: () => void;
  onDiscard: () => void;
}

export function SalesMaterialsBottomBar({
  completedCount,
  totalCount,
  hasChanges,
  saving,
  saved,
  onSave,
  onDiscard,
}: SalesMaterialsBottomBarProps) {
  const percentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  const isComplete = percentage === 100;

  return (
    <div className="sticky bottom-0 z-30 -mx-6 lg:-mx-8 mt-8">
      <div className="h-[2px] bg-gradient-to-r from-[#1875FF]/30 via-[#05E0D1]/30 to-[#1875FF]/15" />

      <div className="bg-white/80 backdrop-blur-xl shadow-[0_-8px_32px_rgba(24,117,255,0.06)]">
        <div className="px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between gap-8">
            <div className="flex items-center gap-4 min-w-0">
              <div className="relative flex-shrink-0 p-2 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-[#1875FF]" />
              </div>

              <div className="flex items-center gap-5 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-bold whitespace-nowrap tracking-tight ${
                    isComplete
                      ? 'bg-clip-text text-transparent bg-gradient-to-r from-[#1875FF] to-[#05E0D1]'
                      : 'text-gray-800'
                  }`}>
                    {completedCount}/{totalCount}
                  </span>
                  <span className="text-sm text-gray-500 whitespace-nowrap hidden sm:inline">
                    sections completed
                  </span>
                </div>

                <div className="relative w-40 sm:w-52 h-2 bg-[#E9FBFF] rounded-full overflow-hidden flex-shrink-0">
                  <div
                    className="h-full rounded-full transition-all duration-700 ease-out relative overflow-hidden"
                    style={{
                      width: `${percentage}%`,
                      background: 'linear-gradient(90deg, #1875FF 0%, #05E0D1 100%)',
                    }}
                  >
                    {!isComplete && percentage > 0 && (
                      <div className="absolute inset-0 progress-shimmer" />
                    )}
                  </div>
                </div>

                <span className="text-xs font-bold tracking-wide whitespace-nowrap bg-clip-text text-transparent bg-gradient-to-r from-[#1875FF] to-[#05E0D1]">
                  {percentage}%
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2.5 flex-shrink-0">
              {saved && !hasChanges && (
                <span className="text-sm text-[#05E0D1] font-semibold flex items-center gap-1.5 animate-fadeIn">
                  <Check className="w-3.5 h-3.5" />
                  Saved
                </span>
              )}

              {hasChanges && (
                <>
                  <div className="w-1.5 h-1.5 rounded-full bg-[#05E0D1] animate-pulse mr-1" />
                  <button
                    onClick={onDiscard}
                    disabled={saving}
                    className="px-3.5 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border border-transparent hover:border-gray-200 hover:bg-gray-50 rounded-xl transition-all duration-200 disabled:opacity-40 flex items-center gap-1.5"
                  >
                    <X className="w-3.5 h-3.5" />
                    Discard
                  </button>
                </>
              )}

              <button
                onClick={onSave}
                disabled={saving || !hasChanges}
                className={`group relative px-5 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2 transition-all duration-300 ${
                  hasChanges
                    ? 'bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white shadow-[0_2px_12px_rgba(24,117,255,0.25)] hover:shadow-[0_4px_20px_rgba(24,117,255,0.35)] hover:scale-[1.02] active:scale-[0.98]'
                    : 'bg-gray-50 text-gray-400 border border-gray-200/60 cursor-not-allowed'
                }`}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
